import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "../../server/routers";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/hooks/useAuth";
import { useApplyDefaultLocation } from "@/hooks/useDefaultLocation";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Clock, Link2, Maximize2, Minimize2, RefreshCw, Tv } from "lucide-react";
import DisplayLinksDialog from "@/components/DisplayLinksDialog";
import { cn } from "@/lib/utils";

/**
 * Admin-only "Weekly Clips" page — a live, wall-monitor-friendly version of
 * the Provider Clips report: every Associated Provider and how many clips
 * they have this week, laid out as a grid sized to fill a 16:9 screen with
 * no scrolling. Auto-refreshes every minute and rolls over to the new week
 * on its own (the "current week" is resolved server-side on every poll).
 *
 * TV mode (button, or ?tv=1 in a bookmark) hides the app header, keeps the
 * screen awake, and offers browser full-screen. Location / week / TV mode
 * are mirrored into the URL so the wall's bookmark reopens the same view.
 *
 * Server: getWeeklyProviderClips in src/server/reports.ts.
 */

const LOCATIONS = ["Iowa", "Minnesota", "Colorado"] as const;
export type Location = (typeof LOCATIONS)[number];

// Same palette as the Location chips elsewhere: Iowa blue, Minnesota purple,
// Colorado green.
const REGION_COLORS: Record<Location, string> = {
  Iowa: "#5B7C99",
  Minnesota: "#6A5ACD",
  Colorado: "#1b6e4a",
};

const REFRESH_MS = 60_000;
/** Earliest week the "jump to week" dropdown offers (the week of Jan 1 2026). */
const FIRST_WEEK = "2025-12-29";
const DAY_LETTERS = ["M", "T", "W", "T", "F", "S", "S"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export type WeeklyData = inferRouterOutputs<AppRouter>["admin"]["weeklyProviderClips"];

function parseIso(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}
function toIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function addDays(iso: string, n: number): string {
  const d = parseIso(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return toIso(d);
}
/** "Oct 12 – 18, 2026" / "Sep 28 – Oct 4, 2026" */
function fmtWeek(startIso: string): string {
  const s = parseIso(startIso);
  const e = parseIso(addDays(startIso, 6));
  const sm = MONTHS[s.getUTCMonth()];
  const em = MONTHS[e.getUTCMonth()];
  const end = sm === em ? `${e.getUTCDate()}` : `${em} ${e.getUTCDate()}`;
  return `${sm} ${s.getUTCDate()} – ${end}, ${e.getUTCFullYear()}`;
}
/** "Oct 5" */
function fmtDay(iso: string): string {
  const d = parseIso(iso);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}
function fmtDuration(totalSeconds: number): string {
  const mins = Math.round(totalSeconds / 60);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}
/** Compact filmed time for a provider card: "45s", "4m 30s", "12m", "1h 05m". */
function fmtFilmed(totalSeconds: number): string {
  const sec = Math.round(totalSeconds);
  if (sec < 60) return `${sec}s`;
  if (sec < 600) {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return s ? `${m}m ${s}s` : `${m}m`;
  }
  const mins = Math.round(sec / 60);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`;
}

function initials(name: string): string {
  const parts = name.replace(/^dr\.?\s+/i, "").split(/\s+/).filter(Boolean);
  return (parts[0]?.[0] ?? "?").toUpperCase() + (parts[1]?.[0] ?? "").toUpperCase();
}
/** "#5B7C99" + alpha → "rgba(91,124,153,a)". */
function hexA(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha.toFixed(2)})`;
}

/** Mondays from FIRST_WEEK up to this week (browser's clock), newest first. */
function weekOptions(): string[] {
  const now = new Date();
  const today = toIso(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
  const d = parseIso(today);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  const out: string[] = [];
  for (let w = toIso(d); w >= FIRST_WEEK; w = addDays(w, -7)) out.push(w);
  return out;
}

function readUrl(): { location: Location | null; week: string | null; tv: boolean } {
  const q = new URLSearchParams(window.location.search);
  const loc = q.get("location");
  const week = q.get("week");
  return {
    location: (LOCATIONS as readonly string[]).includes(loc ?? "") ? (loc as Location) : null,
    week: week && /^\d{4}-\d{2}-\d{2}$/.test(week) ? week : null,
    tv: q.get("tv") === "1",
  };
}

/** Pick a column count so `n` tiles of roughly `tileAspect` (w/h) fill a
 *  `w`×`h` box as fully as possible. */
function bestColumns(n: number, w: number, h: number, tileAspect = 1.9): number {
  if (n <= 1 || w <= 0 || h <= 0) return 1;
  let best = 1;
  let bestScore = -1;
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols);
    const tw = w / cols;
    const th = h / rows;
    // Usable tile = the biggest tileAspect box that fits in each cell.
    const usable = Math.min(tw, th * tileAspect) * Math.min(th, tw / tileAspect);
    if (usable > bestScore) {
      bestScore = usable;
      best = cols;
    }
  }
  return best;
}

/** Keep the screen awake while `enabled` (wall displays). Re-acquired when
 *  the tab becomes visible again, since browsers drop the lock on hide. */
export function useWakeLock(enabled: boolean) {
  useEffect(() => {
    if (!enabled || !("wakeLock" in navigator)) return;
    let lock: { release: () => Promise<void> } | null = null;
    let cancelled = false;
    const acquire = async () => {
      try {
        lock = await (navigator as any).wakeLock.request("screen");
      } catch {
        /* not allowed (e.g. tab hidden) — retried on visibility change */
      }
      if (cancelled) void lock?.release();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") void acquire();
    };
    void acquire();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      void lock?.release();
    };
  }, [enabled]);
}

/** Browser full-screen state + toggle, plus the matching footer button. */
export function FullscreenButton() {
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggle = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen?.();
  };
  return (
    <Button variant="ghost" size="sm" className="gap-1 text-slate-500" onClick={toggle}>
      {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
      {isFullscreen ? "Exit full screen" : "Full screen"}
    </Button>
  );
}

/** Shared query options: only the live week auto-refreshes. */
export function weeklyQueryOptions(week: string | null) {
  return {
    refetchInterval: week ? (false as const) : REFRESH_MS,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    placeholderData: <T,>(prev: T | undefined) => prev,
  };
}

export default function WeeklyClips() {
  const { loading } = useAuth({ redirectOnUnauthenticated: true });
  const initial = useMemo(readUrl, []);

  const [location, setLocation] = useState<Location | null>(initial.location);
  const setLocationCb = useCallback((v: Location) => setLocation(v), []);
  useApplyDefaultLocation(location, setLocationCb);
  // null = "the current week" (live, rolls over on its own).
  const [week, setWeek] = useState<string | null>(initial.week);
  const [tv, setTv] = useState(initial.tv);
  const [linksOpen, setLinksOpen] = useState(false);
  useWakeLock(tv);

  // Mirror the view into the URL so a bookmark reopens it.
  useEffect(() => {
    const q = new URLSearchParams();
    if (location) q.set("location", location);
    if (week) q.set("week", week);
    if (tv) q.set("tv", "1");
    const qs = q.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
  }, [location, week, tv]);

  const query = trpc.admin.weeklyProviderClips.useQuery(
    { location: location ?? "Iowa", weekStart: week ?? undefined },
    { enabled: !!location, ...weeklyQueryOptions(week) },
  );

  const exitTv = () => {
    setTv(false);
    if (document.fullscreenElement) void document.exitFullscreen();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <WeeklyClipsBoard
        location={location}
        data={query.data}
        isFetching={query.isFetching}
        hasError={!!query.error}
        onRefresh={() => void query.refetch()}
        week={week}
        onWeekChange={setWeek}
        appHeader={!tv}
        controls={
          !tv && (
            <>
              <Select value={location ?? ""} onValueChange={(v) => setLocation(v as Location)}>
                <SelectTrigger className="w-[140px] h-9 bg-white">
                  <SelectValue placeholder="Location" />
                </SelectTrigger>
                <SelectContent>
                  {LOCATIONS.map((l) => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="gap-1.5 bg-white" onClick={() => setLinksOpen(true)}>
                <Link2 className="w-4 h-4" />
                Wall links
              </Button>
              <Button size="sm" className="gap-1.5" onClick={() => setTv(true)}>
                <Tv className="w-4 h-4" />
                TV mode
              </Button>
            </>
          )
        }
        footer={
          tv && (
            <>
              <FullscreenButton />
              <Button variant="ghost" size="sm" className="text-slate-500" onClick={exitTv}>
                Exit TV mode
              </Button>
            </>
          )
        }
      />
      <DisplayLinksDialog open={linksOpen} onOpenChange={setLinksOpen} defaultLocation={location} />
    </>
  );
}

/**
 * The Weekly Clips display itself — top bar (title, KPIs, controls), the
 * provider tile grid, and the week navigation. Used by the admin page and
 * by the login-free wall link (pages/WallDisplay.tsx), which supply their
 * own data source and extra controls.
 */
export function WeeklyClipsBoard({
  location,
  data,
  isFetching,
  hasError,
  onRefresh,
  week,
  onWeekChange,
  appHeader,
  controls,
  footer,
}: {
  location: Location | null;
  data: WeeklyData | undefined;
  isFetching: boolean;
  hasError: boolean;
  onRefresh: () => void;
  /** null = live current week. */
  week: string | null;
  onWeekChange: (week: string | null) => void;
  appHeader: boolean;
  controls?: React.ReactNode;
  footer?: React.ReactNode;
}) {
  const weeks = useMemo(weekOptions, []);
  // Landing on the current week → back to live (auto-refreshing) mode.
  const goWeek = (w: string) => onWeekChange(w >= weeks[0] ? null : w);
  const color = REGION_COLORS[location ?? "Iowa"];

  return (
    <div className="h-screen flex flex-col bg-slate-50 text-slate-900 overflow-hidden">
      {appHeader && <AppHeader />}

      <main className="flex-1 min-h-0 flex flex-col px-[1.5vw] py-[1.2vh] gap-[1.2vh]">
        {/* ── Top bar: title + KPIs + controls ─────────────────────────── */}
        <div className="flex items-stretch gap-[1vw]">
          <div className="flex flex-col justify-center pr-[1vw] border-r-4" style={{ borderColor: color }}>
            <div
              className="font-semibold uppercase tracking-wider"
              style={{ color, fontSize: "clamp(11px, 1.1vw, 22px)" }}
            >
              Weekly Clips · {location ?? "—"}
            </div>
            <div className="font-bold leading-tight" style={{ fontSize: "clamp(18px, 2vw, 44px)" }}>
              {data ? (data.isCurrentWeek ? "This Week" : `Week of ${fmtDay(data.weekStart)}`) : "Loading…"}
            </div>
            <div className="text-slate-500" style={{ fontSize: "clamp(11px, 1vw, 20px)" }}>
              {data ? fmtWeek(data.weekStart) : "\u00a0"}
            </div>
          </div>

          {data && (
            <div className="flex-1 grid grid-cols-4 gap-[0.8vw]">
              <Kpi
                label="Clips"
                value={data.totals.clips.toLocaleString()}
                color={color}
                sub={<Delta now={data.totals.clips} prev={data.totals.previousWeekClips} />}
              />
              <Kpi label="Patients filmed" value={data.totals.patients.toLocaleString()} color={color} />
              <Kpi label="Footage time" value={fmtDuration(data.totals.durationSeconds)} color={color} />
              <Kpi
                label="Providers with clips"
                value={`${data.totals.activeProviders}`}
                color={color}
                sub={
                  <span className="text-slate-400">
                    of {data.providers.filter((p) => p.kind === "provider").length}
                  </span>
                }
              />
            </div>
          )}

          <div className="flex flex-col items-end justify-center gap-1.5 shrink-0">
            <div className="flex items-center gap-2">
              {controls}
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 bg-white"
                onClick={onRefresh}
                disabled={isFetching || !location}
              >
                <RefreshCw className={cn("w-4 h-4", isFetching && "animate-spin")} />
                Refresh
              </Button>
            </div>
            <div className="text-slate-500 text-right" style={{ fontSize: "clamp(10px, 0.8vw, 16px)" }}>
              {data && (
                <>
                  Updated{" "}
                  {new Date(data.generatedAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                  {week ? " · past week" : " · auto-refreshes every minute"}
                </>
              )}
              {hasError && <span className="text-red-600"> · couldn&apos;t refresh — retrying</span>}
            </div>
          </div>
        </div>

        {/* ── Provider grid ─────────────────────────────────────────────── */}
        {!location && !isFetching ? (
          <div className="flex-1 flex items-center justify-center text-slate-500">Choose a location to begin.</div>
        ) : data ? (
          <ProviderGrid data={data} color={color} />
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        )}

        {/* ── Bottom bar: revisit previous weeks ───────────────────────── */}
        <div className="flex items-center justify-center gap-2 pt-[0.4vh]">
          <Button
            variant="outline"
            size="sm"
            className="gap-1 bg-white"
            disabled={!data || data.weekStart <= FIRST_WEEK}
            onClick={() => data && goWeek(addDays(data.weekStart, -7))}
          >
            <ChevronLeft className="w-4 h-4" />
            Previous week
          </Button>
          <Select value={data?.weekStart ?? ""} onValueChange={(v) => goWeek(v)} disabled={!data}>
            <SelectTrigger className="w-[270px] h-9 bg-white">
              <SelectValue placeholder="Jump to week" />
            </SelectTrigger>
            <SelectContent>
              {weeks.map((w, i) => (
                <SelectItem key={w} value={w}>
                  {i === 0 ? `This week (${fmtWeek(w)})` : fmtWeek(w)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            className="gap-1 bg-white"
            disabled={!data || data.isCurrentWeek}
            onClick={() => data && goWeek(addDays(data.weekStart, 7))}
          >
            Next week
            <ChevronRight className="w-4 h-4" />
          </Button>
          {week && (
            <Button size="sm" onClick={() => onWeekChange(null)}>
              Back to this week
            </Button>
          )}
          {footer && <div className="ml-4 flex items-center gap-2">{footer}</div>}
        </div>
      </main>
    </div>
  );
}

function Kpi({
  label,
  value,
  color,
  sub,
}: {
  label: string;
  value: string;
  color: string;
  sub?: React.ReactNode;
}) {
  return (
    <div
      className="rounded-lg bg-white border border-slate-200 px-[1vw] py-[0.8vh] flex flex-col justify-center"
      style={{ borderLeft: `4px solid ${color}` }}
    >
      <div className="text-slate-500" style={{ fontSize: "clamp(10px, 0.9vw, 20px)" }}>{label}</div>
      <div className="flex items-baseline gap-2">
        <div className="font-bold leading-none tabular-nums" style={{ fontSize: "clamp(22px, 2.6vw, 64px)" }}>
          {value}
        </div>
        {sub && <div style={{ fontSize: "clamp(10px, 0.9vw, 20px)" }}>{sub}</div>}
      </div>
    </div>
  );
}

function Delta({ now, prev }: { now: number; prev: number }) {
  const d = now - prev;
  if (d === 0) return <span className="text-slate-400">same as last week</span>;
  return (
    <span className={d > 0 ? "text-emerald-600" : "text-slate-400"}>
      {d > 0 ? "▲" : "▼"} {Math.abs(d)} vs last week
    </span>
  );
}

function ProviderGrid({ data, color }: { data: WeeklyData; color: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Measure right away — ResizeObserver's first report can be delayed
    // (e.g. in a background tab), which left the grid blank until then.
    const r = el.getBoundingClientRect();
    setSize({ w: r.width, h: r.height });
    const ro = new ResizeObserver(([e]) => setSize({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const tiles = data.providers;
  const cols = bestColumns(tiles.length, size.w, size.h);
  const rows = Math.max(1, Math.ceil(tiles.length / cols));
  // Bars share one scale across every provider so they're comparable.
  // Filming happens Mon–Fri, so the day bars show the work week only.
  // Saturday/Sunday columns appear automatically in the rare week that
  // actually has weekend clips, so no clip ever silently disappears.
  const dayCount = tiles.some((p) => p.daily[5] > 0 || p.daily[6] > 0) ? 7 : 5;
  const maxDay = Math.max(1, ...tiles.flatMap((p) => p.daily.slice(0, dayCount)));
  const todayIdx = data.isCurrentWeek
    ? Math.round((parseIso(data.today).getTime() - parseIso(data.weekStart).getTime()) / 86_400_000)
    : -1;

  return (
    <div
      ref={ref}
      className="flex-1 min-h-0 grid gap-[0.8vw]"
      style={{
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
      }}
    >
      {tiles.length === 0 && (
        <div className="col-span-full flex items-center justify-center text-slate-500">
          No providers in the {data.location} Provider library yet.
        </div>
      )}
      {/* Wait for the first measurement so the grid doesn't flash as a
          single column before the column count is known. */}
      {size.w > 0 && tiles.map((p) => (
        <ProviderTile key={`${p.kind}:${p.name}`} p={p} color={color} maxDay={maxDay} todayIdx={todayIdx} dayCount={dayCount} />
      ))}
    </div>
  );
}

function ProviderTile({
  p,
  color,
  maxDay,
  todayIdx,
  dayCount,
}: {
  p: WeeklyData["providers"][number];
  color: string;
  maxDay: number;
  todayIdx: number;
  /** 5 (Mon–Fri) normally; 7 when the week has weekend clips. */
  dayCount: number;
}) {
  const muted = p.total === 0 || p.kind !== "provider";
  return (
    // container-type: size → children scale with the tile via cqh/cqw units,
    // so text stays readable whether there are 4 providers or 24.
    <div
      className={cn(
        "rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden flex flex-col",
        p.total === 0 && "opacity-60",
      )}
      style={{ containerType: "size" }}
    >
      <div className="flex-[3] min-h-0 flex items-center gap-[4cqw] px-[5cqw]">
        <Photo p={p} color={color} />
        <div className="flex-1 min-w-0">
          <div
            className={cn("font-semibold leading-tight line-clamp-2 break-words", p.kind !== "provider" && "italic text-slate-600")}
            style={{ fontSize: "min(10cqh, 6.5cqw)" }}
          >
            {p.name}
          </div>
          {p.total > 0 && p.seconds > 0 && (
            <div
              className="flex items-center gap-[1.5cqw] font-medium text-slate-600 tabular-nums mt-[1.5cqh]"
              style={{ fontSize: "min(8.5cqh, 5.5cqw)" }}
              title="Combined length of this provider's clips this week"
            >
              <Clock className="shrink-0" style={{ width: "1em", height: "1em" }} />
              {fmtFilmed(p.seconds)} filmed
            </div>
          )}
          {p.kind === "unlisted" && (
            <div className="text-slate-400 truncate" style={{ fontSize: "min(6cqh, 4.5cqw)" }}>
              not in this location&apos;s library
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div
            className="font-bold leading-none tabular-nums"
            style={{ fontSize: "min(32cqh, 20cqw)", color: muted ? "#94a3b8" : color }}
          >
            {p.total}
          </div>
          <div className="text-slate-400" style={{ fontSize: "min(6cqh, 4.5cqw)" }}>
            clip{p.total === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {/* Mon → Fri mini bars (Sat/Sun only in weeks with weekend clips) */}
      <div
        className="flex-[2] min-h-0 grid gap-[1.5cqw] px-[5cqw] pb-[4cqh] pt-[1cqh] border-t border-slate-100"
        style={{ gridTemplateColumns: `repeat(${dayCount}, minmax(0, 1fr))` }}
      >
        {p.daily.slice(0, dayCount).map((n, i) => (
          <div key={i} className="flex flex-col items-center justify-end min-h-0">
            <div className="tabular-nums text-slate-600 leading-none" style={{ fontSize: "min(6.5cqh, 4cqw)" }}>
              {n > 0 ? n : ""}
            </div>
            <div className="w-full flex-1 min-h-0 flex items-end mt-[1cqh]">
              <div
                className="w-full rounded-sm"
                style={{
                  height: n > 0 ? `${Math.max(8, (n / maxDay) * 100)}%` : "2px",
                  backgroundColor: n > 0 ? hexA(color, 0.35 + 0.55 * (n / maxDay)) : "#e2e8f0",
                }}
              />
            </div>
            <div
              className={cn("leading-none mt-[1cqh]", i === todayIdx ? "font-bold" : "text-slate-400")}
              style={{ fontSize: "min(6cqh, 4cqw)", color: i === todayIdx ? color : undefined }}
            >
              {DAY_LETTERS[i]}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Photo({ p, color }: { p: WeeklyData["providers"][number]; color: string }) {
  const style = { width: "min(46cqh, 24cqw)", height: "min(46cqh, 24cqw)" };
  if (p.photoUrl) {
    return <img src={p.photoUrl} alt="" className="rounded-full object-cover shrink-0 border border-slate-200" style={style} />;
  }
  return (
    <div
      className="rounded-full shrink-0 flex items-center justify-center font-semibold"
      style={{
        ...style,
        fontSize: "min(16cqh, 8cqw)",
        ...(p.kind === "provider"
          ? { backgroundColor: hexA(color, 0.15), color }
          : { backgroundColor: "#f1f5f9", color: "#94a3b8" }),
      }}
    >
      {p.kind === "none" ? "–" : initials(p.name)}
    </div>
  );
}
